/*
  <nbd-variant-content> shows the child marked data-variant-id for the selected
  variant and hides the rest, so per variant content can be rendered in Liquid
  and still follow the variant picker.

  Follows variant changes from two sources, so it works without a theme preset:
  variant:change on the product form named by data-form-id (nbd-buy and themes
  that emit it, detail { variant }), and change events from an input named "id"
  inside the same theme section (themes that update the form's hidden variant
  input, such as Dawn).

  With data-event set, each change also fires that event, bubbling, with the
  JSON in the matching child's data-detail as its detail.

  Load once per page:
  <script type="module" src="{{ 'nbd-variant-content.js' | asset_url }}" defer></script>
*/

class NBDVariantContent extends HTMLElement {
  constructor() {
    super();
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
  }

  connectedCallback() {
    this.productForm = this.dataset.formId ? document.forms[this.dataset.formId] : null;
    this.scope = this.closest('.shopify-section') || document;
    this.productForm?.addEventListener('variant:change', this.handleVariantChange);
    this.scope.addEventListener('change', this.handleInputChange);
    this.current = null;
    // Deferred modules such as nbd-selling-plans add their listeners on upgrade,
    // so wait for them before the first event.
    const init = () => this.select(this.dataset.variantId);
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init, { once: true });
    } else {
      init();
    }
  }

  disconnectedCallback() {
    this.productForm?.removeEventListener('variant:change', this.handleVariantChange);
    this.scope?.removeEventListener('change', this.handleInputChange);
  }

  handleVariantChange(event) {
    this.select(event.detail?.variant?.id);
  }

  handleInputChange(event) {
    if (event.target.name !== 'id') return;
    this.select(event.target.value);
  }

  select(id) {
    if (!id || String(id) === this.current) return;
    const items = [...this.querySelectorAll(':scope > [data-variant-id]')];
    const match = items.find((item) => item.dataset.variantId === String(id));
    // An id from another product on the page leaves the current content alone.
    if (!match) return;
    this.current = String(id);
    items.forEach((item) => (item.hidden = item !== match));
    if (!this.dataset.event) return;
    let detail = null;
    try {
      detail = JSON.parse(match.dataset.detail || 'null');
    } catch (e) {}
    this.dispatchEvent(new CustomEvent(this.dataset.event, { bubbles: true, detail }));
  }
}

if (!customElements.get('nbd-variant-content')) {
  customElements.define('nbd-variant-content', NBDVariantContent);
}
